/**
 * Вид «список» раздела «Товары»: размеры товаров пачками.
 *
 * Спека — `docs/specs/2026-09-16-products-list-view-design.md`. Числа считает
 * сервер теми же функциями, что у карточки товара (`GET /api/products/sizes`),
 * здесь — только какие строки рисовать и какие пачки спросить.
 */

import { type WindowValue, windowSpan } from "@/lib/reorder";
import type { SpeedWindow } from "@/components/sizeCells";
import { apiFetch, query } from "@/lib/api";
import type { FbwSupply } from "@/lib/supply";
import type { OrderEta } from "@/lib/orderEta";
import type { NavPlatform } from "@/lib/nav/labels";

/** Пачка запроса. 40 товаров стоят 0,26 с на боевой базе, весь ассортимент —
 *  3,6 с (замер 16.09.26): список спрашивает то, что видно, а не всё сразу. */
export const CHUNK = 50;

export type SizeReorder = {
  need: number;
  need_wb: number;
  need_ozon: number;
  gap_wb: number;
  gap_ozon: number;
  gap: number;
  order: number;
};

/**
 * Партия-дата остатка плана размера — строка панели «в планах» (`card.py`,
 * `plan_dates`). `left` = `planned − ordered`, и сумма `left` равна `plan_left`.
 */
export type PlanDate = {
  plan_date_id: number;
  plan_date: string | null;
  planned: number;
  ordered: number;
  left: number;
  supplier_id: number | null;
  /** Партия «Активный»; иначе — частично отгружена и ещё ждёт остаток. */
  date_active: boolean;
};

export type SizeCard = {
  card_id: number;
  size: string;
  /**
   * Размер снят в CRM. Доезжает только у товара, где живых карточек не
   * осталось вовсе, — у остальных сервер такие строки не отдаёт.
   */
  deleted: boolean;
  /** `null` — строки снимка у размера нет: это НЕ ноль на складе. */
  wb_stock: number | null;
  wb_to_client: number | null;
  wb_from_client: number | null;
  /** К покупателю + от покупателя; `null`, если неизвестно хоть одно. */
  wb_transit: number | null;
  wb_synced_at: string | null;
  /** Штук размера в наших поставках WB без приёмки — строка «поставка». */
  wb_supply: number;
  /** `null` — этого размера на Ozon нет. */
  ozon_sku: number | null;
  ozon_stock: number | null;
  ozon_to_client: number | null;
  ozon_returns: number | null;
  /** К покупателю + возврат; `null`, если неизвестно хоть одно. */
  ozon_transit: number | null;
  ozon_synced_at: string | null;
  korob: number;
  plan_left: number;
  plans: number;
  /** Партии плана размера — панель по клику на «в планах». */
  plan_dates?: PlanDate[];
  in_orders: number;
  /** Заказы размера с расчётным приходом — панель по клику на «в заказах». */
  orders_detail: OrderEta[];
  stale: number;
  ordered: number;
  accepted: number;
  by_klass: Record<string, number>;
  /** Уходимость по окнам «7» · «30» · «60» · «90»; пусто — движений не было. */
  sell: Record<string, SpeedWindow>;
  reorder: Record<string, SizeReorder>;
  /** Сколько довезти на полку WB; `null` — короб пуст либо снимка размера нет. */
  shelf_need: number | null;
  /** То же для полки Ozon (`stock/shelf_ozon.py`), короб тот же. */
  ozon_shelf_need?: number | null;
  /** Доля времени без наличия за окно `zd`; `null` — журнала нет (прочерк). */
  wb_zero?: ZeroShare | null;
  ozon_zero?: ZeroShare | null;
};

/** Доля времени без наличия (`stock/shelf_zero.py`): процент и дней под ним. */
export type ZeroShare = { pct: number; days: number };

/**
 * Ячейка строки «без наличия». `null` — ячейка пуста: размер весь период был на
 * полке (так велел владелец) — либо журнала нет, тогда `dash` и прочерк.
 * Без подсветки (разметка 29.09.26 09:58 п.1): рыжий и красный давали пестроту.
 */
export function zeroCell(z: ZeroShare | null | undefined): { text: string; dash: boolean } | null {
  if (z === null || z === undefined) return { text: "—", dash: true };
  if (z.pct <= 0) return null;
  // Меньше половины процента округлилось бы в «0 %» и читалось бы как «всегда был».
  return { text: z.pct < 0.5 ? "<1%" : `${Math.round(z.pct)}%`, dash: false };
}

/** Размер в принятой поставке. `arrived` `null` — журнала остатка нет: прочерк. */
export type RecentCard = {
  declared: number;
  accepted: number;
  ready_for_sale: number;
  arrived: number | null;
  verdict: "arrived" | "partial" | "missing" | null;
};

/**
 * Принятая поставка WB (`supply/recent.py`, спека
 * `docs/specs/2026-09-25-calc-recent-supplies-design.md`): встало ли принятое
 * на остаток. Окно прироста — 48 часов от начала приёмки, моменты в UTC.
 */
export type RecentSupply = {
  supply_id: number;
  warehouse: string | null;
  /** 4 — идёт приёмка, 5 — принято. */
  status_id: number;
  fact_date: string;
  since: string | null;
  until: string | null;
  /** Ключ — `card_id` строкой; размера нет — в поставке его не было. */
  cards: Record<string, RecentCard>;
};

/**
 * Куда пишет строка «план на ДД.ММ» списка (`plan/today.py`, правило «А»):
 * дата, партия (`null` — запись заведёт новую) и план размера в ней целиком.
 */
export type PlanDay = {
  date: string;
  plan_date_id: number | null;
  /** Ключ — `card_id` строкой; нет ключа — строки плана у размера нет. */
  planned: Record<string, number>;
};

export type ProductSizes = {
  product_id: number;
  /** Есть ли у товара SKU на Ozon — признак строк Ozon, как блока FBO карточки. */
  ozon: boolean;
  /** Поставки WB, в которых товар едет; пусто — строки «поставка» нет. */
  wb_supplies: FbwSupply[];
  /** Последние принятые поставки WB (до трёх за 30 дней) — строки «сдали». */
  wb_recent?: RecentSupply[];
  /** Поставщик товара — подпись под фото; `null` — не проставлен. */
  supplier?: { id: number; name: string } | null;
  /** `null` — поставщика нет либо свободной даты нет в 14 днях. */
  plan_day?: PlanDay | null;
  /** Причина, по которой запись плана закрыта; `null` — открыта. Ставит загрузчик
   *  из общего поля ответа: строка ввода знает это ДО нажатия. */
  plan_write_blocked?: string | null;
  /** Партии плана товара — объединение партий размеров, свёртка сервера. */
  plan_dates?: PlanDate[];
  /** Заказы товара — объединение заказов размеров, свёртка сервера. */
  orders_detail?: OrderEta[];
  /** Модель, склейка и артикул WB — строка номеров под фото. */
  numbers?: { model_id: number | null; model_group_id: number | null; article: string | null };
  /** Цель и «клиент платит» под номерами (`list_prices.py`); площадки нет — `null`. */
  prices?: ListPrices;
  cards: SizeCard[];
};

/** Цена товара в строке списка — `app/core/modules/catalog/product/list_prices.py`. */
export type ListPrices = {
  wb: {
    /** Наша цель WB; `null` — не назначена. */
    target: number | null;
    /** Что стоит на витрине сейчас. */
    price: number | null;
    /** Клиент платит: цена сейчас × (1 − СПП последней недели с продажами). */
    pays: number | null;
    spp: number | null;
    /** Вердикт сервера «цель дошла»; `null` — сравнивать не с чем. */
    matches: boolean | null;
    /** Последняя смена цели (UTC без пояса); `null` — цели нет или дата неизвестна. */
    changed_at?: string | null;
  } | null;
  ozon: {
    target: number | null;
    target_max: number | null;
    price: number | null;
    price_max?: number | null;
    pays: number | null;
    pays_max?: number | null;
    spp: number | null;
    /** Последняя смена целей Ozon по SKU (UTC без пояса). */
    changed_at?: string | null;
    /** Паритет кассы клиента с WB (`list_prices.паритет`); `null` — нет кассы одной из площадок. */
    parity?: ListParity | null;
  } | null;
};

/** Касса клиента Ozon против WB и цена Ozon, при которой они сравняются. */
export type ListParity = {
  /** Касса Ozon ÷ касса WB − 1, %: минус — на Ozon клиент платит меньше. */
  gap: number;
  /** Цена продавца Ozon для паритета, ₽. */
  price: number;
  /** Сдвиг цены Ozon до паритета, %. */
  pct: number;
  /** В пределах допуска «Сравнения WB / Ozon» (±2 п.п.). */
  ok: boolean;
};

/** Подпись строки поставки: «Воронеж · сдали 24.09», у идущей приёмки — «принимают». */
export function recentLabel(s: RecentSupply): string {
  const склад = (s.warehouse ?? "склад ?").replace(/^СЦ\s+/, "");
  const [, m, d] = s.fact_date.split("-");
  return `${склад} · ${s.status_id === 4 ? "принимают" : "сдали"} ${d}.${m}`;
}

/** Ячейка «в продаже → прибыло». `null` — размера в поставке не было. */
export function recentCell(c: RecentCard | undefined): { text: string; tone: "err" | "warn" | null } | null {
  if (!c) return null;
  const прибыло = c.arrived === null ? "—" : String(c.arrived);
  const tone = c.verdict === "missing" ? "err" : c.verdict === "partial" ? "warn" : null;
  return { text: `${c.ready_for_sale} → ${прибыло}`, tone };
}

/**
 * Итог поставки по товару. Цвет — ПО ХУДШЕМУ РАЗМЕРУ, а не по сумме: лишний
 * приход одного размера (возврат покупателя) не должен прятать размер, который
 * не встал вовсе. Нет журнала хоть у одного — прибыло прочерком.
 */
export function recentTotal(s: RecentSupply): { text: string; tone: "err" | "warn" | null } {
  const все = Object.values(s.cards);
  const вПродаже = все.reduce((a, c) => a + c.ready_for_sale, 0);
  const прибыло = все.some((c) => c.arrived === null) ? "—" : String(все.reduce((a, c) => a + (c.arrived ?? 0), 0));
  const tone = все.some((c) => c.verdict === "missing") ? "err" : все.some((c) => c.verdict === "partial") ? "warn" : null;
  return { text: `${вПродаже} → ${прибыло}`, tone };
}

export async function loadSizes(
  ids: number[],
  days: number,
  w?: WindowValue,
  zd?: number,
): Promise<ProductSizes[]> {
  // Свой период уходимости — в запрос: его ячейки сервер считает отдельно.
  // Пресеты едут в ответе всегда, и слать их незачем.
  const свой = w !== undefined && windowSpan(w) ? String(w) : undefined;
  const got = await apiFetch<{ items: ProductSizes[]; plan_write_blocked?: string | null }>(
    `/api/products/sizes${query({ ids: ids.join(","), days, w: свой, zd })}`,
  );
  return got.items.map((item) => ({ ...item, plan_write_blocked: got.plan_write_blocked ?? null }));
}

/** Подпись строки ввода плана: сегодня — словом, иначе «план на 26.09». */
export function planDayLabel(date: string, today: string): string {
  if (date === today) return "план на сегодня";
  const [, m, d] = date.split("-");
  return `план на ${d}.${m}`;
}

/** Сегодня по часам браузера, `YYYY-MM-DD` — только для подписи «сегодня». */
export const localToday = (): string => new Date().toLocaleDateString("sv-SE");

/** Потолок числа ручки плана (`PlanLineIn.qty`, `le=65535`). */
const PLAN_QTY_MAX = 65535;

/**
 * Что набрано в поле плана: `null` — пусто, «не трогаем»; число — план размера
 * на дату ЦЕЛИКОМ (ноль снимает); `"bad"` — не целое, минус или выше потолка.
 */
export function parsePlanQty(raw: string): number | null | "bad" {
  const text = raw.trim();
  if (text === "") return null;
  if (!/^\d+$/.test(text)) return "bad";
  const qty = Number(text);
  return qty > PLAN_QTY_MAX ? "bad" : qty;
}

/**
 * Записать план ОДНОГО размера на дату — ручкой плана карточки
 * (`POST /api/product/{id}/plan`), нового места записи в CRM нет. Сухой ответ
 * (запись закрыли между загрузкой и нажатием) — ошибка с причиной.
 */
export async function savePlan(productId: number, date: string, cardId: number, qty: number): Promise<void> {
  const done = await apiFetch<{ dry_run?: boolean; blocked_by?: string | null }>(`/api/product/${productId}/plan`, {
    method: "POST",
    body: JSON.stringify({ plan_date: date, lines: [{ card_id: cardId, qty }], apply: true }),
  });
  if (done.dry_run) throw new Error(done.blocked_by ?? "запись не выполнена: сервер ответил сухим прогоном");
}

export type SizeLine =
  | "wb_head"
  | "wb_stock"
  | "wb_to_client"
  | "wb_from_client"
  | "wb_supply"
  | "wb_zero"
  | "ozon_head"
  | "ozon_stock"
  | "ozon_to_client"
  | "ozon_returns"
  | "ozon_zero"
  | "sell"
  | "buy"
  | "korob"
  | "plan"
  | "orders"
  | "reorder"
  | "shelf"
  | "recent";

/**
 * Какие строки рисовать.
 *
 * СТРОКИ ИЗ ОДНИХ НУЛЕЙ НЕ ВЫВОДЯТСЯ (замечание владельца 16.09.26: «если в
 * заказах нет товаров, эту строчку можно не выводить»), но только те, где ноль
 * значит «ничего не будет»: короба, планы, заказы и «к заказу». Остатки и
 * «продадим» стоят всегда — ноль на складе и есть сигнал «товара нет», ради
 * которого вид заводится. Зависший заказ («!») — сигнал и при нулевом числе.
 *
 * ПЛОЩАДКА — ГРУППОЙ С ЗАГОЛОВКОМ (разметка 25.09.26 11:03 п. 1, как блок FBO
 * карточки): заголовок несёт итог «в системе» — то же число, что на фото, — а
 * под ним «на складе · едет · возвращается · в поставке». «Едет» — только к
 * покупателю: прежнее «в пути» включало и возврат, и тот стоял дважды.
 */
/**
 * «Довести» размера на площадке таблицы: Ozon — полка Ozon, WB и «обе» — полка
 * WB (разметка 20.10.26 12:31 п.2: подсорт Ozon смотрит довоз своего склада).
 */
export function shelfNeedOf(c: SizeCard, platform: NavPlatform): number | null {
  return platform === "ozon" ? (c.ozon_shelf_need ?? null) : c.shelf_need;
}

export function linesOf(p: ProductSizes, w: WindowValue, shelf = true, platform: NavPlatform = "both"): SizeLine[] {
  const out: SizeLine[] = ["wb_head", "wb_stock", "wb_to_client", "wb_from_client"];
  // «ПОСТАВКА» — ЧТО УЖЕ ЕДЕТ НА WB, та же строка, что у блока FBW карточки
  // (разметка владельца 24.09.26). Ничего не едет — строки нет.
  if (p.wb_supplies.length > 0) out.push("wb_supply");
  const any = (test: (c: SizeCard) => boolean) => p.cards.some(test);
  // «БЕЗ НАЛИЧИЯ» ПОСЛЕДНЕЙ СТРОКОЙ ПЛОЩАДКИ (разметка 29.09.26 08:10 п.1).
  // Все размеры всегда на полке — строки нет: ячейки у неё были бы пусты.
  if (any((c) => (c.wb_zero?.pct ?? 0) > 0)) out.push("wb_zero");
  if (p.ozon) {
    out.push("ozon_head", "ozon_stock", "ozon_to_client", "ozon_returns");
    if (any((c) => (c.ozon_zero?.pct ?? 0) > 0)) out.push("ozon_zero");
  }
  out.push("sell");
  // ЗАКУПКИ ОДНИМ БЛОКОМ (разметка 25.09.26 11:03 п. 2): короба, планы, заказы —
  // всё, что уже куплено или покупается. Короба стояли под «продадим».
  const закупки: SizeLine[] = [];
  if (any((c) => c.korob !== 0)) закупки.push("korob");
  if (any((c) => c.plan_left !== 0)) закупки.push("plan");
  if (any((c) => c.in_orders !== 0 || c.stale > 0)) закупки.push("orders");
  // «ИТОГО В ЗАКУПКЕ» ПЕРВОЙ СТРОКОЙ БЛОКА (разметка 29.09.26 07:43 п.3):
  // короба + планы + заказы — сравнить глазом с «продадим» выше. Только при
  // двух строках и больше: одну строку итог повторил бы число в число.
  if (закупки.length > 1) out.push("buy");
  out.push(...закупки);
  if (any((c) => (c.reorder[String(w)]?.order ?? 0) !== 0)) out.push("reorder");
  // «ДОВЕСТИ» СТОИТ ПОСЛЕ «К ЗАКАЗУ»: числа отвечают на разные вопросы — «чем
  // закрыть полку WB сейчас» и «сколько заказать фабрике», — и порядок строк это
  // говорит. Прочерк («везти нечем») строки не заводит, как и ноль.
  // «Довоз: нет» (разметка 29.09.26 07:43 п.4) — строки нет вовсе: `shelf`.
  if (shelf && any((c) => (shelfNeedOf(c, platform) ?? 0) > 0)) out.push("shelf");
  // «СДАЛИ» — ПОСЛЕДНИМИ (разметка 25.09.26 08:19 п. 2): принятые поставки и
  // встало ли принятое на остаток. Рисует их только «Расчёт» (`calc`).
  if ((p.wb_recent ?? []).length > 0) out.push("recent");
  return out;
}

/** «Итого в закупке» размера: короба + остаток планов + в заказах. */
export const buyOf = (c: SizeCard): number => c.korob + c.plan_left + c.in_orders;

/** Первая видимая строка группы — над ней толстая линия. Заголовки есть только
 *  у площадок: там строка-заголовок несёт итог «в системе» и не пустует. */
export function groupStarts(lines: SizeLine[]): Set<SizeLine> {
  const out = new Set<SizeLine>(["wb_head", "ozon_head", "sell"]);
  const first = (group: SizeLine[]) => lines.find((line) => group.includes(line));
  // Закупки, дальше вывод из них — «к заказу» и «довести» — своей группой.
  for (const group of [["buy", "korob", "plan", "orders"], ["reorder", "shelf"]] as SizeLine[][]) {
    const got = first(group);
    if (got) out.add(got);
  }
  // Поставки — своя группа: это история отгрузок, а не то, что ещё закажем.
  if (lines.includes("recent")) out.add("recent");
  return out;
}

/** Пачки к запросу: задетые видимым диапазоном `first…last` и одна вперёд — без
 *  номеров, которые `skip` велит пропустить (приехали, летят, отказали). */
export function chunksToLoad(
  order: number[],
  first: number,
  last: number,
  skip: (id: number) => boolean,
): number[][] {
  const out: number[][] = [];
  for (let chunk = Math.floor(first / CHUNK); chunk <= Math.floor(last / CHUNK) + 1; chunk++) {
    const ids = order.slice(chunk * CHUNK, (chunk + 1) * CHUNK).filter((id) => !skip(id));
    if (ids.length) out.push(ids);
  }
  return out;
}
